import { useCallback, useState } from 'react';

import { useCancelBatchMutation } from './api';

export interface BatchCancelController {
  isCancelling: boolean;
  errorMessage?: string;
  cancel: () => void;
}

/**
 * Stopping a running batch (AC-14).
 *
 * Cancel stops what has not left the building. Already-sent messages, their
 * touchpoints and the patient requirements are untouched — the batch stops
 * sending, it does not rewrite what already happened — and the panel says so
 * in the cancelled state rather than leaving staff to infer it.
 *
 * **No duplicate-click handling, deliberately.** The route answers 200 with the
 * batch's current state when it is already cancelled or already finished,
 * rather than 409: a second click, or one that lost the race to the runner's
 * own finish, is not an error and the contract says so. Inventing a guard here
 * would turn a designed non-event into a failure message.
 */
export const useBatchCancel = (
  organizationId: string | null | undefined,
  definitionId: string,
  batchId: string | undefined,
  onCancelled: () => void,
): BatchCancelController => {
  const [cancelBatch, { isLoading: isCancelling }] = useCancelBatchMutation();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const cancel = useCallback(() => {
    if (!organizationId || !batchId) return;

    setErrorMessage(undefined);
    cancelBatch({ orgId: organizationId, definitionId, batchId })
      .unwrap()
      .then(() => {
        // The response carries the cancelled batch with its final counts, but
        // the panel reads the query rather than this result, so the query is
        // told to re-read. Polling has already stopped by then — the state is
        // no longer running — which is why this cannot be left to the timer.
        onCancelled();
      })
      .catch((error: unknown) => {
        const detail = (error as { data?: { message?: string } })?.data?.message;
        setErrorMessage(
          detail ?? 'The send could not be stopped. Messages may still be going out.',
        );
      });
  }, [cancelBatch, organizationId, definitionId, batchId, onCancelled]);

  return { isCancelling, errorMessage, cancel };
};
